import {
  clearActiveSupabaseAuthStorage,
  clearImpersonationStorage,
  IS_IMPERSONATION_SESSION,
  supabase,
} from "../lib/supabase";
import { shouldRefreshSession } from "../utils/authSession";
import {
  appendSessionHash,
  assertHandoffAllowed,
  isTrustedAuthHandoffTarget,
} from "../utils/authHandoff";

function isExpiredOrInvalidSessionError(error) {
  return /Invalid Refresh Token|JWT expired|session_not_found/i.test(
    String(error?.message || error || ""),
  );
}

class AuthService {
  async signUp(email, password, userData = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData,
        },
      });

      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(error?.message || "Failed to create account");
    }
  }

  async signInWithOtp(email, options = {}) {
    try {
      const { data, error } = await supabase?.auth?.signInWithOtp({
        email,
        options: {
          emailRedirectTo: options.redirectTo || window.location.origin,
          ...options,
        },
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(error?.message || "Failed to send authentication code");
    }
  }

  async verifyOtp(email, token, type = "email") {
    try {
      const { data, error } = await supabase?.auth?.verifyOtp({
        email,
        token,
        type,
      });
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(error?.message || "Invalid authentication code");
    }
  }

  async signOut() {
    try {
      // Cross-origin handoff removes the source copy, so revoking the current
      // session is sufficient and does not sign the user out on other devices.
      await supabase?.auth?.signOut({ scope: "local" });
    } catch (error) {
      console.warn(
        "SignOut encountered an error (safe to ignore):",
        error?.message,
      );
    }
    // Supabase can fail before clearing storage when the network or session is
    // already invalid. Logout must still remove this origin's persisted copy.
    if (typeof window !== "undefined") {
      supabase.auth.stopAutoRefresh();
      clearActiveSupabaseAuthStorage();
      if (IS_IMPERSONATION_SESSION) {
        sessionStorage.removeItem("admin_impersonation");
        clearImpersonationStorage();
      }
    }
    // Always clear any remaining auth cookies manually as a fallback
    if (typeof document !== "undefined" && !IS_IMPERSONATION_SESSION) {
      const cookiesToClear = ["sb-auth-token", "supabase-auth-token"];
      const domains = [
        ".procertcompliance.com",
        ".skillcompliance.com",
        ".skillcompliance.ca",
        ".isogyaan.com",
        "",
      ];
      cookiesToClear.forEach((name) => {
        domains.forEach((domain) => {
          document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domain ? `; domain=${domain}` : ""}`;
        });
      });
    }
  }

  async handoffSession(
    targetUrl,
    session = null,
    partnerDomain = "procertcompliance.com",
  ) {
    if (!isTrustedAuthHandoffTarget(targetUrl, partnerDomain)) {
      throw new Error("Unsafe authentication handoff destination");
    }

    assertHandoffAllowed({
      targetUrl,
      currentOrigin: window.location.origin,
      impersonating: IS_IMPERSONATION_SESSION,
    });

    const resolvedTarget = new URL(targetUrl, window.location.origin);
    if (resolvedTarget.origin === window.location.origin) {
      window.location.assign(resolvedTarget.toString());
      return;
    }

    const activeSession = session || (await this.getSession());
    if (!activeSession?.access_token || !activeSession?.refresh_token) {
      throw new Error("Authentication session is missing or expired");
    }

    const handoffUrl = appendSessionHash(
      resolvedTarget.toString(),
      activeSession,
    );

    // Do not call signOut here: it would revoke the refresh token that the
    // destination needs. Remove only this origin's persisted copy, leaving a
    // single browser origin responsible for the transferred session.
    supabase.auth.stopAutoRefresh();
    clearActiveSupabaseAuthStorage();
    window.location.replace(handoffUrl);
  }

  async getCurrentUser() {
    try {
      const {
        data: { user },
        error,
      } = await supabase?.auth?.getUser();
      if (error) throw error;
      return user;
    } catch (error) {
      // It's common to not have a user if not logged in, but we handle errors
      return null;
    }
  }

  async getSession() {
    try {
      const {
        data: { session },
        error,
      } = await supabase?.auth?.getSession();
      if (error) throw error;
      return session;
    } catch (error) {
      return null;
    }
  }

  // Mutex to prevent concurrent refresh calls (which cause token rotation conflicts)
  _refreshPromise = null;

  /**
   * Refreshes the Supabase client's internal auth state so that
   * subsequent supabase.functions.invoke() calls use a fresh token.
   * Call this before any functions.invoke() to prevent 401s.
   * Returns true if a valid session exists, false otherwise.
   */
  async ensureFreshToken() {
    const session = await this.getValidSession();
    return !!session;
  }

  /**
   * Gets a session with a valid (refreshed) access token.
   * Unlike getSession(), this ensures the token is not expired by
   * refreshing first, and validates the token if refresh returns nothing.
   * Uses a mutex to prevent concurrent refresh calls that would
   * invalidate each other due to refresh token rotation.
   */
  async getValidSession() {
    // If a refresh is already in progress, wait for it instead of starting another
    if (this._refreshPromise) {
      return this._refreshPromise;
    }

    this._refreshPromise = this._doGetValidSession();
    try {
      return await this._refreshPromise;
    } finally {
      this._refreshPromise = null;
    }
  }

  async _doGetValidSession() {
    const cachedSession = await this.getSession();
    if (cachedSession?.access_token && !shouldRefreshSession(cachedSession)) {
      return cachedSession;
    }

    // First, try to refresh the session to get a fresh access token
    let refreshError = null;
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (data?.session) {
        return data.session;
      }
      if (error) {
        refreshError = error;
        console.warn("Session refresh returned error:", error.message);
      }
    } catch (refreshErr) {
      refreshError = refreshErr;
      console.warn("Session refresh threw:", refreshErr.message);
    }

    if (isExpiredOrInvalidSessionError(refreshError)) {
      await this.signOut();
      return null;
    }

    // Refresh failed — check if the cached session is still valid
    if (!cachedSession?.access_token) {
      return null;
    }

    // Validate the cached token by calling getUser() which hits the auth server
    try {
      const {
        data: { user },
        error,
      } = await supabase.auth.getUser(cachedSession.access_token);
      if (error || !user) {
        if (isExpiredOrInvalidSessionError(error)) {
          await this.signOut();
        }
        return null;
      }
      return cachedSession;
    } catch {
      return null;
    }
  }

  async updateProfile(updates) {
    try {
      const { data, error } = await supabase?.auth?.updateUser(updates);
      if (error) throw error;
      return data;
    } catch (error) {
      throw new Error(error?.message || "Failed to update profile");
    }
  }

  async resetPassword(email) {
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/auth/reset-password`,
      });
      if (error) throw error;
    } catch (error) {
      throw new Error(error?.message || "Failed to send reset email");
    }
  }

  async signUpWithPassword(email, password, userData = {}, options = {}) {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo:
            options.emailRedirectTo ||
            `${window.location?.origin}/auth/verify-email`,
        },
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  async signInWithGoogle(redirectTo) {
    const { data, error } = await supabase?.auth?.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: redirectTo || `${window.location.origin}/login`,
        queryParams: { prompt: "select_account" },
      },
    });
    if (error) throw error;
    return data;
  }

  async signInWithPassword(email, password) {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  async resendVerificationEmail(email, options = {}) {
    try {
      const { error } = await supabase?.auth?.resend({
        type: "signup",
        email,
        options,
      });
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  }

  onAuthStateChange(callback) {
    if (!supabase?.auth) {
      return { data: { subscription: { unsubscribe: () => {} } } };
    }
    return supabase.auth.onAuthStateChange(callback);
  }
}

export const authService = new AuthService();
